import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useManagement, useUpsertManagement } from "@/hooks/api/useManagement";

type Field = { key: string; label: string; type?: "text" | "number" | "date" | "checkbox" };

// Mirrors legacy `object_beheer_general` — providers + customer numbers, the CV
// (boiler) block, access codes, and the VvE (owners' association) contact.
const SECTIONS: { title: string; fields: Field[] }[] = [
  {
    title: "Fee",
    fields: [
      { key: "management_fee_percentage", label: "Management fee (%)", type: "number" },
    ],
  },
  {
    title: "Utilities",
    fields: [
      { key: "electricity_provider", label: "Electricity provider" },
      { key: "electricity_customer_no", label: "Electricity customer no." },
      { key: "gas_provider", label: "Gas provider" },
      { key: "gas_customer_no", label: "Gas customer no." },
      { key: "water_provider", label: "Water provider" },
      { key: "water_customer_no", label: "Water customer no." },
      { key: "heating_provider", label: "Heating provider" },
      { key: "heating_customer_no", label: "Heating customer no." },
      { key: "internet_provider", label: "Internet provider" },
      { key: "internet_customer_no", label: "Internet customer no." },
      { key: "tv_provider", label: "TV provider" },
      { key: "tv_customer_no", label: "TV customer no." },
      { key: "network_provider", label: "Network provider" },
      { key: "network_customer_no", label: "Network customer no." },
    ],
  },
  {
    title: "CV / boiler",
    fields: [
      { key: "cv_maintenance_contract", label: "Maintenance contract", type: "checkbox" },
      { key: "cv_contract_ref", label: "Contract reference" },
      { key: "cv_customer_no", label: "Customer no." },
      { key: "cv_phone", label: "Contractor phone" },
      { key: "cv_email", label: "Contractor email" },
      { key: "maintenance_date", label: "Last maintenance", type: "date" },
    ],
  },
  {
    title: "Access & security",
    fields: [
      { key: "alarm_code", label: "Alarm code" },
      { key: "key_code", label: "Key / lockbox code" },
      { key: "key_control", label: "Keys held by us", type: "checkbox" },
      { key: "internet_ssid", label: "WiFi SSID" },
      { key: "internet_password", label: "WiFi password" },
    ],
  },
  {
    title: "VvE (owners' association)",
    fields: [
      { key: "vve_name", label: "VvE name" },
      { key: "vve_contact_name", label: "Contact name" },
      { key: "vve_phone", label: "Phone" },
      { key: "vve_email", label: "Email" },
    ],
  },
];

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);
type FormState = Record<string, string>;

export function ManagementCard({ propertyId }: { propertyId: string }) {
  const { data: record } = useManagement(propertyId);
  const upsert = useUpsertManagement(propertyId);
  const [form, setForm] = useState<FormState>({});

  useEffect(() => {
    if (record) {
      const next: FormState = {};
      for (const { key, type } of ALL_FIELDS) {
        const v = (record as Record<string, unknown>)[key];
        next[key] = type === "checkbox" ? (v ? "1" : "") : v == null ? "" : String(v);
      }
      setForm(next);
    }
  }, [record]);

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = { is_managed: true };
    for (const { key, type } of ALL_FIELDS) {
      const v = form[key];
      if (type === "checkbox") {
        body[key] = v === "1";
      } else if (v === "" || v == null) {
        continue;
      } else {
        body[key] = type === "number" ? Number(v) : v;
      }
    }
    upsert.mutate(body, {
      onSuccess: () => toast.success("Management details saved"),
      onError: () => toast.error("Could not save"),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Management (in beheer)</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSave} className="space-y-6">
          {SECTIONS.map((section) => (
            <div key={section.title} className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section.title}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {section.fields.map(({ key, label, type }) =>
                  type === "checkbox" ? (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form[key] === "1"}
                        onChange={(e) =>
                          setForm({ ...form, [key]: e.target.checked ? "1" : "" })
                        }
                        className="size-4 rounded border-input accent-primary"
                      />
                      {label}
                    </label>
                  ) : (
                    <div key={key} className="space-y-1.5">
                      <Label htmlFor={key}>{label}</Label>
                      <Input
                        id={key}
                        type={type ?? "text"}
                        value={form[key] ?? ""}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      />
                    </div>
                  ),
                )}
              </div>
            </div>
          ))}
          <Button type="submit" disabled={upsert.isPending}>
            {upsert.isPending ? "Saving…" : "Save management details"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
